import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { DIRECTIONS } from "@repo/ui/constants/directions";
import { SSR_CODES } from "@repo/ui/constants/ssrs";

import type { RootState } from "@repo/store/store";

import { useAppSelector } from "../hooks";

export interface Extras {
  flexiSmart?: FlexiSmartItem;
  boardingCard?: PassengerBoardingCard;
  priorityBoarding?: PassengerBoardingCard;
  petOnBoard?: PassengerBoardingCard;
}

export interface PrevExtras {
  outbound: {
    passenger?: string;
    services?: string[];
  }[];
  inbound: {
    passenger?: string;
    services?: string[];
  }[];
}

export interface ExtrasState {
  checkInCart: {
    outbound?: Extras;
    inbound?: Extras;
    buttonStatus?: ButtonStatus;
    confirmed: boolean;
    insurance?: number;
  };
  prevExtras: PrevExtras;
  bookingCart: {
    outbound?: Extras;
    inbound?: Extras;
    buttonStatus?: ButtonStatus;
    confirmed: boolean;
    insurance?: number;
  };
  insurance?: number | null;
  prevInsurance?: number | null;
  insuranceData?: InsuranceData | null;
  // Stores the quote/offer returned from the insurance provider (Chubb)
  insuranceQuote?: InsuranceQuote | null;
  showInsuranceError: boolean;
  showInsuranceModal: boolean;
  showBoardingPassSelectError: boolean;
}

export interface InsuranceData {
  isSelected: boolean;
  price: number;
  totalPrice?: number;
  quoteId: string;
  planCode?: string;
  planName?: string;
  regionCode?: string;
  certificateNumber?: string;
  masterPolicyNumber?: string;
  acceptConditions: boolean;
  passengerFeeKeys?: Record<string, string>;
}

// Insurance quote object as returned by Chubb (or derived from it)
export interface InsuranceQuote {
  pricePerPerson: number;
  totalPrice: number;
  quoteId: string;
  planCode?: string;
  planName?: string;
  regionCode?: string;
}

export interface BoardingPassSelections {
  [segmentId: string]: string | null;
}

export interface PetOnBoardItem {
  price: number;
  ssrKey: string;
  alreadyAdded?: boolean;
  ssrCode?: string;
  passengerKey?: string;
}

export interface BoardingPassItem {
  price: number;
  ssrKey: string;
  alreadyAdded?: boolean;
  ssrCode?: string;
  passengerKey?: string;
}

export interface FlexiSmartItem {
  price: number;
  ssrKey: string;
  alreadyAdded?: boolean;
  ssrCode?: string;
  passengerKey?: string;
}

export interface PriorityBoardingItem {
  price: number;
  ssrKey: string;
  alreadyAdded?: boolean;
  ssrCode?: string;
  passengerKey?: string;
}

export type BoardingCardValue =
  PetOnBoardItem | BoardingPassItem | PriorityBoardingItem | FlexiSmartItem;

export interface PassengerBoardingCard {
  [passengerId: string]: BoardingCardValue;
}

export interface BoardingCardSelections {
  [segmentId: string]: {
    [passengerId: string]: {
      selectedOptionId: string | null;
      price: number;
      isIncluded: boolean;
    };
  };
}

export interface PriorityBoardingSelections {
  [direction: string]: {
    [passengerId: string]: boolean;
  };
}

export interface PetOnBoardSelections {
  [direction: string]: {
    [passengerId: string]: boolean;
  };
}

export interface ButtonStatus {
  flexiSmartButtonStatus?: Record<string, boolean>;
  boardingPassSelections?: BoardingPassSelections;
  priorityBoardingSelections?: PriorityBoardingSelections;
  petOnBoardSelections?: PetOnBoardSelections;
  insuranceButton?: boolean;
}

const initialState: ExtrasState = {
  checkInCart: {
    outbound: {},
    inbound: {},
    confirmed: false,
  },
  prevExtras: {
    outbound: [],
    inbound: [],
  },
  bookingCart: {
    outbound: {},
    inbound: {},
    confirmed: false,
  },
  insurance: null,
  prevInsurance: null,
  insuranceData: null,
  insuranceQuote: null,
  showInsuranceError: false,
  showInsuranceModal: false,
  showBoardingPassSelectError: false,
};

const extrasSlice = createSlice({
  name: "extras",
  initialState,
  reducers: {
    confirmExtrasSelections: (state, action: PayloadAction<"checkInCart" | "bookingCart">) => {
      const cartType = action.payload;

      state[cartType].confirmed = true;
    },
    unconfirmExtrasSelections: (state, action: PayloadAction<"checkInCart" | "bookingCart">) => {
      const cartType = action.payload;

      state[cartType].confirmed = false;
    },
    setBoardingPassSelection: (
      state,
      action: PayloadAction<{
        segmentId: string;
        optionId: string | null;
        price?: number;
        isIncluded?: boolean;
        direction?: string;
        passengerId?: string;
        cartType: "checkInCart" | "bookingCart";
        ssrKey?: string;
        ssrCode?: string;
        alreadyAdded?: boolean;
      }>,
    ) => {
      const {
        segmentId,
        optionId,
        price = 0,
        isIncluded,
        direction,
        passengerId = "Adult 1",
        cartType,
        ssrKey = "",
        ssrCode,
        alreadyAdded = false,
      } = action.payload;

      // Ensure cart exists
      state[cartType].buttonStatus ??= {};
      state[cartType].buttonStatus.boardingPassSelections ??= {};
      state[cartType].buttonStatus.boardingPassSelections[segmentId] = optionId;

      // Determine which extras object to update based on direction
      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const segmentKey = isOutbound ? "outbound" : "inbound";

      // Update the appropriate segment
      state[cartType][segmentKey] ??= {};
      state[cartType][segmentKey].boardingCard ??= {};
      state[cartType][segmentKey].boardingCard[passengerId] = isIncluded
        ? { price: 0, ssrKey: "" }
        : { price: price, ssrKey: ssrKey, ssrCode, passengerKey: passengerId, alreadyAdded };
    },
    clearBoardingPassSelections: (state, action: PayloadAction<"checkInCart" | "bookingCart">) => {
      const cartType = action.payload;
      // Ensure cart exists

      if (state[cartType].buttonStatus?.boardingPassSelections) {
        state[cartType].buttonStatus.boardingPassSelections = {};
      }

      // Reset boardingCard for both outbound and inbound
      ["outbound", "inbound"].forEach((segmentKey) => {
        if (state[cartType][segmentKey as "outbound" | "inbound"]) {
          state[cartType][segmentKey as "outbound" | "inbound"]!.boardingCard = {};
        }
      });
    },
    clearPetOnBoarding: (
      state,
      action: PayloadAction<{
        direction: string;
        passengerKey: string;
        cartType: "checkInCart" | "bookingCart";
      }>,
    ) => {
      const { direction, passengerKey, cartType } = action.payload;
      // Ensure cart exists

      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const segmentKey = isOutbound ? "outbound" : "inbound";
      const segment = state[cartType][segmentKey];

      // Clean up pet on board for this passenger if it exists
      if (segment?.petOnBoard?.[passengerKey] !== undefined) {
        delete segment.petOnBoard[passengerKey];

        // Clean up empty pet on board object if no more passengers
        if (Object.keys(segment.petOnBoard).length === 0) {
          delete segment.petOnBoard;
        }
      }

      // Also clear the button status selection so the hydration useEffect
      // does not re-add the pet from stale booking data
      if (
        state[cartType].buttonStatus?.petOnBoardSelections?.[direction]?.[passengerKey] !==
        undefined
      ) {
        state[cartType].buttonStatus.petOnBoardSelections[direction][passengerKey] = false;
      }
    },
    clearPriorityBoarding: (
      state,
      action: PayloadAction<{
        direction: string;
        passengerKey: string;
        cartType: "checkInCart" | "bookingCart";
      }>,
    ) => {
      const { direction, passengerKey, cartType } = action.payload;
      // Ensure cart exists

      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const segmentKey = isOutbound ? "outbound" : "inbound";
      const segment = state[cartType][segmentKey];

      // Clean up priority boarding for this passenger if it exists
      if (segment?.priorityBoarding?.[passengerKey] !== undefined) {
        delete segment.priorityBoarding[passengerKey];

        // Clean up empty priority boarding object if no more passengers
        if (Object.keys(segment.priorityBoarding).length === 0) {
          delete segment.priorityBoarding;
        }
      }

      if (
        state[cartType].buttonStatus?.priorityBoardingSelections?.[direction]?.[passengerKey] !==
        undefined
      ) {
        state[cartType].buttonStatus.priorityBoardingSelections[direction][passengerKey] = false;
      }
    },
    clearBoardingCard: (
      state,
      action: PayloadAction<{
        direction: string;
        passengerKey: string;
        segmentId?: string;
        cartType: "checkInCart" | "bookingCart";
      }>,
    ) => {
      const { direction, passengerKey, cartType } = action.payload;
      // Ensure cart exists

      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const segmentKey = isOutbound ? "outbound" : "inbound";
      const segment = state[cartType][segmentKey];

      // Clean up boarding card for this passenger if it exists
      if (segment?.boardingCard?.[passengerKey] !== undefined) {
        delete segment.boardingCard[passengerKey];

        // Clean up empty boarding card object if no more passengers
        if (Object.keys(segment.boardingCard).length === 0) {
          delete segment.boardingCard;
        }
      }
    },
    setFlexiSmartButtonStatus: (
      state,
      action: PayloadAction<{
        isAdded: boolean;
        price: number;
        direction?: string;
        cardId: string;
        cartType: "checkInCart" | "bookingCart";
        ssrKey?: string;
        ssrCode?: string;
      }>,
    ) => {
      const { isAdded, price, direction, cardId, cartType, ssrKey = "", ssrCode } = action.payload;

      const segmentKey = direction === DIRECTIONS.OUTBOUND ? "outbound" : "inbound";

      state[cartType].buttonStatus ??= {};
      state[cartType].buttonStatus.flexiSmartButtonStatus ??= {};
      state[cartType][segmentKey] ??= {};

      state[cartType].buttonStatus.flexiSmartButtonStatus[cardId] = isAdded;

      if (isAdded) {
        state[cartType][segmentKey].flexiSmart = {
          price,
          ssrKey,
          ssrCode,
        };
      } else {
        // REMOVE flexiSmart for outbound / inbound
        delete state[cartType][segmentKey].flexiSmart;
      }
    },

    setPriorityBoardingButtonStatus: (
      state,
      action: PayloadAction<{
        isAdded: boolean;
        price: string | number;
        direction?: string;
        passengerId?: string;
        ssrKey?: string;
        cartType: "checkInCart" | "bookingCart";
        alreadyAdded?: boolean;
        ssrCode?: string;
      }>,
    ) => {
      const {
        isAdded,
        price,
        direction = "",
        passengerId = "Adult 1",
        cartType,
        ssrKey = "",
        alreadyAdded = false,
        ssrCode,
      } = action.payload;
      const segmentKey = direction === DIRECTIONS.OUTBOUND ? "outbound" : "inbound";

      // Ensure cart exists
      state[cartType].buttonStatus ??= {};
      state[cartType].buttonStatus.priorityBoardingSelections ??= {};
      state[cartType].buttonStatus.priorityBoardingSelections[direction] ??= {};
      state[cartType].buttonStatus.priorityBoardingSelections[direction][passengerId] = isAdded;

      // Update the appropriate segment's priorityBoarding value
      if (isAdded) {
        state[cartType][segmentKey] ??= {};
        state[cartType][segmentKey].priorityBoarding ??= {};
        state[cartType][segmentKey].priorityBoarding[passengerId] = {
          price: Number(price),
          ssrKey,
          passengerKey: passengerId,
          ssrCode,
          alreadyAdded,
        };
      } else if (state[cartType][segmentKey]?.priorityBoarding?.[passengerId] !== undefined) {
        delete state[cartType][segmentKey].priorityBoarding[passengerId];

        // Clean up if no more priority boarding for this segment
        if (Object.keys(state[cartType][segmentKey].priorityBoarding || {}).length === 0) {
          delete state[cartType][segmentKey].priorityBoarding;
        }
      }
    },
    setPetOnBoardButtonStatus: (
      state,
      action: PayloadAction<{
        isAdded: boolean;
        price: number;
        direction: string;
        passengerId?: string;
        cartType: "checkInCart" | "bookingCart";
        ssrKey: string;
        ssrCode: string;
        alreadyAdded?: boolean;
      }>,
    ) => {
      const {
        isAdded,
        price,
        direction,
        passengerId = "Adult 1",
        cartType,
        ssrKey,
        ssrCode,
        alreadyAdded = false,
      } = action.payload;
      const segmentKey = direction === DIRECTIONS.OUTBOUND ? "outbound" : "inbound";

      // Ensure cart exists
      state[cartType].buttonStatus ??= {};
      state[cartType].buttonStatus.petOnBoardSelections ??= {};
      state[cartType].buttonStatus.petOnBoardSelections[direction] ??= {};
      state[cartType].buttonStatus.petOnBoardSelections[direction][passengerId] = isAdded;

      // Update the appropriate segment's petOnBoard value
      if (isAdded) {
        state[cartType][segmentKey] ??= {};
        state[cartType][segmentKey].petOnBoard ??= {};
        state[cartType][segmentKey].petOnBoard[passengerId] = {
          price,
          ssrKey,
          ssrCode,
          passengerKey: passengerId,
          alreadyAdded,
        };
      } else if (state[cartType][segmentKey]?.petOnBoard?.[passengerId] !== undefined) {
        delete state[cartType][segmentKey].petOnBoard[passengerId];

        // Clean up if no more pet on board for this segment
        if (Object.keys(state[cartType][segmentKey].petOnBoard || {}).length === 0) {
          delete state[cartType][segmentKey].petOnBoard;
        }
      }
    },
    setPrevExtras: (state, action: PayloadAction<PrevExtras>) => {
      state.prevExtras = action.payload;
    },
    setInsuranceButtonStatus: (
      state,
      action: PayloadAction<{
        isAdded: boolean;
        cartType: "checkInCart" | "bookingCart";
      }>,
    ) => {
      const { isAdded, cartType } = action.payload;
      // Ensure cart exists
      state[cartType].buttonStatus ??= {};
      state[cartType].buttonStatus!.insuranceButton = isAdded;
      state.insurance = action.payload ? 5.507 : 0;
    },
    setInsuranceState: (state, action: PayloadAction<InsuranceData>) => {
      state.insuranceData = action.payload;
      // Keep legacy sync if needed, or rely on this new source of truth
      state.insurance = action.payload.isSelected ? action.payload.price : 0;
    },
    clearInsuranceState: (state) => {
      state.insuranceData = null;
      state.insurance = 0;
    },
    setShowInsuranceError: (state, action: PayloadAction<boolean>) => {
      state.showInsuranceError = action.payload;
    },
    setShowInsuranceModal: (state, action: PayloadAction<boolean>) => {
      state.showInsuranceModal = action.payload;
    },
    // Store insurance quote data fetched from the provider
    setInsuranceQuote: (state, action: PayloadAction<InsuranceQuote | null>) => {
      state.insuranceQuote = action.payload;
    },
    setInsurancePassengerFeeKeys: (state, action: PayloadAction<Record<string, string>>) => {
      if (state.insuranceData) {
        state.insuranceData.passengerFeeKeys = {
          ...state.insuranceData.passengerFeeKeys,
          ...action.payload,
        };
      }
    },
    clearExtras: (state) => {
      return {
        ...initialState,
        prevExtras: state.prevExtras,
      };
    },
    setShowBoardingPassSelectError: (state, action: PayloadAction<boolean>) => {
      state.showBoardingPassSelectError = action.payload;
    },
  },
});

// Hooks
export const useExtrasState = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType] ?? { confirmed: false });
};

// Utility hook to determine cart type based on booking details
export const useExtrasCartType = (): "checkInCart" | "bookingCart" =>
  useAppSelector((state) => {
    const isCheckin = state.booking?.isCheckIn ?? false;
    return isCheckin ? "checkInCart" : "bookingCart";
  });

// Convenience hooks that automatically use the correct cart type
export const useCurrentExtrasState = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType] ?? { confirmed: false });
};

export const useCurrentOutboundExtrasSelection = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.outbound);
};

export const useCurrentInboundExtrasSelection = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.inbound);
};

export const useCurrentExtrasConfirmed = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.confirmed ?? false);
};

export const useCurrentBoardingPassSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus?.boardingPassSelections);
};

export const useCurrentBoardingPassSelection = (segmentId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.buttonStatus?.boardingPassSelections?.[segmentId],
  );
};

export const useCurrentBoardingCardPrice = (
  passengerId: string,
  direction: "outbound" | "inbound",
) => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const item = state.extras?.[cartType]?.[direction]?.boardingCard?.[passengerId];
    if (!item) return 0;
    return typeof item === "number" ? item : item.price;
  });
};

export const useCurrentTotalBoardingCardPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const boardingCard = state.extras?.[cartType]?.[direction]?.boardingCard;
    if (!boardingCard) return 0;

    return Object.values(boardingCard).reduce<number>((sum, item) => {
      if (!item) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

export const useCurrentButtonStatus = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus);
};

export const useCurrentPriorityBoardingSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.buttonStatus?.priorityBoardingSelections,
  );
};

export const useCurrentPriorityBoardingSelection = (direction: string, passengerId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) =>
      state.extras?.[cartType]?.buttonStatus?.priorityBoardingSelections?.[direction]?.[
        passengerId
      ],
  );
};

export const useCurrentPetOnBoardSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus?.petOnBoardSelections);
};

export const useCurrentPetOnBoardSelection = (direction: string, passengerId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) =>
      state.extras?.[cartType]?.buttonStatus?.petOnBoardSelections?.[direction]?.[passengerId],
  );
};

export const useCurrentPriorityBoardingPrice = (
  passengerId: string,
  direction: "outbound" | "inbound",
) => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const item = state.extras?.[cartType]?.[direction]?.priorityBoarding?.[passengerId];
    if (!item) return 0;
    return typeof item === "number" ? item : item.price;
  });
};

export const useCurrentTotalPriorityBoardingPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const priorityBoarding = state.extras?.[cartType]?.[direction]?.priorityBoarding;
    if (!priorityBoarding) return 0;

    return Object.values(priorityBoarding).reduce<number>((sum, item) => {
      if (!item) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

export const useCurrentPetOnBoardPrice = (
  passengerId: string,
  direction: "outbound" | "inbound",
) => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const item = state.extras?.[cartType]?.[direction]?.petOnBoard?.[passengerId];
    if (!item) return 0;
    return typeof item === "number" ? item : item.price;
  });
};

export const useCurrentTotalPetOnBoardPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const petOnBoard = state.extras?.[cartType]?.[direction]?.petOnBoard;
    if (!petOnBoard) return 0;
    return Object.values(petOnBoard).reduce((sum: number, item) => {
      if (item === undefined) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

export const useOutboundExtrasSelection = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.outbound);
};

export const useInboundExtrasSelection = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.inbound);
};

export const useExtrasConfirmed = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.confirmed ?? false);
};

export const useBoardingPassSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus?.boardingPassSelections);
};

// Utility hook to get boarding pass selection for a specific segment
export const useBoardingPassSelection = (segmentId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.buttonStatus?.boardingPassSelections?.[segmentId],
  );
};

// Hook to get boarding card price for a specific passenger
export const useBoardingCardPrice = (passengerId: string, direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.[direction]?.boardingCard?.[passengerId] ?? 0,
  );
};

// Hook to get total boarding card price for all passengers
export const useTotalBoardingCardPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const boardingCard = state.extras?.[cartType]?.[direction]?.boardingCard;
    if (!boardingCard) return 0;

    return Object.values(boardingCard).reduce<number>((sum, item) => {
      if (!item) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

// Hook to get common button status
export const useButtonStatus = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus);
};

// Hook to get priority boarding selections
export const usePriorityBoardingSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.buttonStatus?.priorityBoardingSelections,
  );
};

// Utility hook to get priority boarding selection for a specific direction and passenger
export const usePriorityBoardingSelection = (direction: string, passengerId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) =>
      state.extras?.[cartType]?.buttonStatus?.priorityBoardingSelections?.[direction]?.[
        passengerId
      ],
  );
};

// Hook to get pet on board selections
export const usePetOnBoardSelections = () => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => state.extras?.[cartType]?.buttonStatus?.petOnBoardSelections);
};

// Utility hook to get pet on board selection for a specific direction
export const usePetOnBoardSelection = (direction: string, passengerId: string) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) =>
      state.extras?.[cartType]?.buttonStatus?.petOnBoardSelections?.[direction]?.[passengerId],
  );
};

// Hook to get priority boarding price for a specific passenger
export const usePriorityBoardingPrice = (
  passengerId: string,
  direction: "outbound" | "inbound",
) => {
  const cartType = useExtrasCartType();
  return useAppSelector(
    (state) => state.extras?.[cartType]?.[direction]?.priorityBoarding?.[passengerId] ?? 0,
  );
};

// Hook to get total priority boarding price for all passengers
export const useTotalPriorityBoardingPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const priorityBoarding = state.extras?.[cartType]?.[direction]?.priorityBoarding;
    if (!priorityBoarding) return 0;

    return Object.values(priorityBoarding).reduce<number>((sum, item) => {
      if (!item) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

// Hook to get pet on board price for a specific passenger
export const usePetOnBoardPrice = (passengerId: string, direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const petOnBoard = state.extras?.[cartType]?.[direction]?.petOnBoard?.[passengerId];
    if (!petOnBoard) return 0;
    return typeof petOnBoard === "number" ? petOnBoard : petOnBoard.price;
  });
};

// Hook to get total pet on board price for all passengers
export const useTotalPetOnBoardPrice = (direction: "outbound" | "inbound") => {
  const cartType = useExtrasCartType();
  return useAppSelector((state) => {
    const petOnBoard = state.extras?.[cartType]?.[direction]?.petOnBoard;
    if (!petOnBoard) return 0;
    return Object.values(petOnBoard).reduce((sum: number, item) => {
      if (item === undefined) return sum;
      const price = typeof item === "number" ? item : item.price;
      return sum + (price || 0);
    }, 0);
  });
};

export const selectAddedExtras = createSelector(
  [(state: RootState) => state.booking?.isCheckIn ?? false, (state: RootState) => state.extras],
  (isCheckIn, extras) => {
    const cart = isCheckIn ? extras.checkInCart : extras.bookingCart;
    return buildAddedExtrasFromCart(cart);
  },
);

export interface AddedExtras {
  ssrCode: string;
  passengerKey: string;
  direction: "outbound" | "inbound";
  amount?: number;
}

export const buildAddedExtrasFromCart = (
  cart: ExtrasState["checkInCart"] | ExtrasState["bookingCart"],
): AddedExtras[] => {
  const buildForSegment = (
    segment: Extras | undefined,
    direction: "outbound" | "inbound",
  ): AddedExtras[] => {
    if (!segment) return [];

    const result: AddedExtras[] = [];

    const processItems = (items?: Record<string, BoardingCardValue>) => {
      if (!items) return;

      Object.values(items).forEach((item) => {
        if (!item || item.alreadyAdded || !item.ssrCode) return;

        result.push({
          ssrCode: item.ssrCode,
          passengerKey: item.passengerKey ?? "",
          direction,
          amount: item.price,
        });
      });
    };

    // Passenger-level SSRs
    processItems(segment.boardingCard);
    processItems(segment.priorityBoarding);
    processItems(segment.petOnBoard);

    // FlexiSmart → direction-level SSR
    if (segment.flexiSmart && !segment.flexiSmart.alreadyAdded) {
      result.push({
        ssrCode: SSR_CODES.FLEXI,
        passengerKey: segment.flexiSmart.passengerKey ?? "",
        direction,
      });
    }

    return result.filter((x) => x.ssrCode);
  };

  return [
    ...buildForSegment(cart.outbound, "outbound"),
    ...buildForSegment(cart.inbound, "inbound"),
  ];
};
// Hook to get insurance state
export const useInsuranceState = () => {
  return useAppSelector((state) => state.extras.insuranceData);
};

export const useShowBoardingPassSelectError = () => {
  return useAppSelector((state) => state.extras.showBoardingPassSelectError);
};

export const {
  confirmExtrasSelections,
  unconfirmExtrasSelections,
  setBoardingPassSelection,
  clearBoardingPassSelections,
  clearPetOnBoarding,
  clearPriorityBoarding,
  clearBoardingCard,
  setFlexiSmartButtonStatus,
  setPriorityBoardingButtonStatus,
  setPetOnBoardButtonStatus,
  setInsuranceButtonStatus,
  setInsuranceState,
  clearInsuranceState,
  setShowInsuranceError,
  setShowInsuranceModal,
  setInsuranceQuote,
  setInsurancePassengerFeeKeys,
  clearExtras,
  setPrevExtras,
  setShowBoardingPassSelectError,
} = extrasSlice.actions;

export const selectShowInsuranceError = (state: RootState) => state.extras.showInsuranceError;

export const selectShowInsuranceModal = (state: RootState) => state.extras.showInsuranceModal;

// Selector to get insurance quote
export const selectInsuranceQuote = (state: RootState) => state.extras.insuranceQuote;

// Hook to get the insurance quote from store
export const useInsuranceQuote = () => useAppSelector((state) => state.extras.insuranceQuote);

export default extrasSlice.reducer;
